import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../../lib/screenshotPreview';
import '../../components/ContextualMedia.css';

export const useGuideTheme = () => {
  const { theme, dsColors, colors } = useTheme();
  return {
    theme,
    dsColors,
    colors,
    heroPrimary: colors.textPrimary,
    heroSecondary: colors.textSecondary,
    cardBorder: theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border,
  };
};

export const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
export const sectionTitle = 'text-2xl sm:text-3xl font-bold mb-4';
export const sectionLead = 'text-base sm:text-lg max-w-2xl leading-relaxed';

export const shotUrl = (theme: 'dark' | 'light', base: string) =>
  `/assets/screenshots/ios-delivery/${base} - ${theme === 'dark' ? 'Dark' : 'Light'}.png`;

export const deviceFrame = 'rounded-[2rem] border-4 overflow-hidden shadow-2xl';

export interface GuideShot {
  base: string;
  alt: string;
  caption?: string;
}

/** Theme-aware app screenshot with an accurate caption. */
export const Shot: React.FC<{ shot: GuideShot; className?: string }> = ({ shot, className }) => {
  const { theme, heroSecondary } = useGuideTheme();
  const deviceBorder = theme === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)';
  return (
    <figure className={className}>
      <img
        src={getScreenshotPreview(shotUrl(theme, shot.base))}
        srcSet={getScreenshotPreviewSet(shotUrl(theme, shot.base))}
        sizes="min(15rem, 60vw)"
        alt={shot.alt}
        width={1320}
        height={2868}
        loading="lazy"
        className={`${deviceFrame} w-full max-w-[min(15rem,60vw)] mx-auto`}
        style={{ borderColor: deviceBorder }}
      />
      {shot.caption && (
        <figcaption className="mt-3 text-xs text-center max-w-[min(15rem,60vw)] mx-auto" style={{ color: heroSecondary }}>
          {shot.caption}
        </figcaption>
      )}
    </figure>
  );
};

export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => {
  const { colors, cardBorder } = useGuideTheme();
  return (
    <div
      className={`p-6 rounded-xl ${className}`}
      style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}` }}
    >
      {children}
    </div>
  );
};

export const BulletList: React.FC<{ items: string[] }> = ({ items }) => {
  const { dsColors, heroSecondary } = useGuideTheme();
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }} aria-hidden="true">•</span>
          <span className="text-sm" style={{ color: heroSecondary }}>{item}</span>
        </li>
      ))}
    </ul>
  );
};

/** "You'll need" prerequisites strip — quiet, above the numbered steps. */
export const Prereqs: React.FC<{ items: string[] }> = ({ items }) => {
  const { dsColors, heroPrimary, heroSecondary, colors, cardBorder } = useGuideTheme();
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-lg px-4 py-3 mb-8 text-sm"
      style={{ backgroundColor: colors.surface, border: `1px dashed ${cardBorder}` }}
    >
      <span className="font-bold uppercase tracking-wider text-xs mr-1" style={{ color: dsColors.primary }}>
        You'll need
      </span>
      {items.map((item, i) => (
        <span key={item} className="inline-flex items-baseline gap-2">
          <span style={{ color: heroSecondary }}>{item}</span>
          {i < items.length - 1 && <span aria-hidden="true" style={{ color: heroPrimary }}>·</span>}
        </span>
      ))}
    </div>
  );
};

export const NumSteps: React.FC<{ steps: { title: string; description: string }[] }> = ({ steps }) => {
  const { dsColors, heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <ol className="space-y-5">
      {steps.map((step, i) => (
        <li key={step.title} className="flex gap-4 items-start">
          <span
            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white"
            style={{ backgroundColor: dsColors.primary }}
            aria-hidden="true"
          >
            {i + 1}
          </span>
          <div>
            <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>{step.title}</h3>
            <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{step.description}</p>
          </div>
        </li>
      ))}
    </ol>
  );
};

export interface IllustratedStep {
  title: string;
  description: string;
  shot?: GuideShot;
}

/** Numbered steps; each step's capture renders inside its own unit after the copy. */
export const IllustratedSteps: React.FC<{ steps: IllustratedStep[]; start?: number }> = ({ steps, start = 1 }) => {
  const { dsColors, heroPrimary, heroSecondary } = useGuideTheme();
  return (
    <div className="guide-steps">
      <ol>
        {steps.map((step, i) => (
          <li key={step.title} className={step.shot ? 'gs-shot' : 'gs-plain'}>
            <div className="flex gap-4 items-start">
              <span
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white"
                style={{ backgroundColor: dsColors.primary }}
                aria-hidden="true"
              >
                {start + i}
              </span>
              <div>
                <h3 className="font-semibold text-base mb-0.5" style={{ color: heroPrimary }}>{step.title}</h3>
                <p className="text-sm" style={{ color: heroSecondary, textWrap: 'pretty' }}>{step.description}</p>
              </div>
            </div>
            {step.shot && <Shot shot={step.shot} className="mt-4" />}
          </li>
        ))}
      </ol>
    </div>
  );
};

/** Context copy → its shot → following copy; wide screens place the shot in a
   narrow column spanning the context + following rows. */
export const GuideMediaComposition: React.FC<{ context: React.ReactNode; shot: GuideShot; following?: React.ReactNode }> = ({ context, shot, following }) => (
  <div className="guide-media">
    <div className="gm-inner">
      <div className="gm-context">{context}</div>
      <div className="gm-media">
        <Shot shot={shot} />
      </div>
      {following && <div className="gm-following">{following}</div>}
    </div>
  </div>
);

/** Quiet "expected result" confirmation under the actions. */
export const ResultNote: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { dsColors } = useGuideTheme();
  return (
    <p
      className="mt-8 inline-block text-sm font-semibold px-4 py-2 rounded-lg"
      style={{ backgroundColor: `${dsColors.successStrong}1f`, color: dsColors.successStrong }}
    >
      {children}
    </p>
  );
};

export const Troubleshooting: React.FC<{ items: string[] }> = ({ items }) => {
  const { dsColors, heroPrimary, colors, cardBorder } = useGuideTheme();
  return (
    <div
      className="mt-8 rounded-xl p-5"
      style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}`, borderLeft: `3px solid ${dsColors.warningStrong}` }}
    >
      <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: dsColors.warningStrong }}>
        Troubleshooting
      </h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="text-sm" style={{ color: heroPrimary }}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

export const RelatedLinks: React.FC<{ links: { id: string; label: string }[] }> = ({ links }) => {
  const { dsColors, heroSecondary } = useGuideTheme();
  return (
    <p className="mt-8 text-sm" style={{ color: heroSecondary }}>
      <span className="font-semibold mr-2">Related guides:</span>
      {links.map((l, i) => (
        <span key={l.id}>
          {i > 0 && <span aria-hidden="true" className="mx-1.5">·</span>}
          <a
            href={`#${l.id}`}
            className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
            style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
          >
            {l.label}
          </a>
        </span>
      ))}
    </p>
  );
};

export const MarketingLink: React.FC<{ to: string; label: string }> = ({ to, label }) => {
  const { dsColors } = useGuideTheme();
  return (
    <Link
      to={to}
      className="text-sm font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
      style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
    >
      ← Back to {label}
    </Link>
  );
};
